import { AutocompleteSelect } from '@/components/ui/AutocompleteSelect'
import { Button } from '@/components/ui/Button'
import { Fieldset } from '@/components/ui/Fieldset'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { geolocateApi } from '@/features/geolocate/api/geolocateApi'
import { toastService } from '@/lib/toastService'
import { HStack, Stack } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { districtsApi, districtsBigApi, streetTypesApi, streetsApi } from '../api/streetsApi'

/**
 * Street Modal Component
 * Form for creating/editing streets
 */
export function StreetModal({ isOpen, onClose, street }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isEdit = !!street
  const [selectedCity, setSelectedCity] = useState(null)
  const [selectedDistrictBig, setSelectedDistrictBig] = useState(null)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      street: '',
      full_street: '',
      type_street_group: null,
      city: null,
      district_big: null,
      district: null,
    },
  })

  const cityValue = watch('city')
  const districtBigValue = watch('district_big')

  // Reset dependent fields when city changes
  useEffect(() => {
    if (cityValue && cityValue.id !== selectedCity?.id) {
      setValue('district_big', null)
      setValue('district', null)
      setSelectedCity(cityValue)
      setSelectedDistrictBig(null)
    }
  }, [cityValue, selectedCity, setValue])

  // Reset district when district_big changes
  useEffect(() => {
    if (districtBigValue && districtBigValue.id !== selectedDistrictBig?.id) {
      setValue('district', null)
      setSelectedDistrictBig(districtBigValue)
    }
  }, [districtBigValue, selectedDistrictBig, setValue])

  // Populate form when editing
  useEffect(() => {
    if (street) {
      const cityObj = street.city ? { id: street.city_id, name: street.city_name } : null
      const districtBigObj = street.district_big ? { id: street.district_big_id, district_big: street.district_big_name } : null
      const districtObj = street.district ? { id: street.district_id, district: street.district_name } : null
      const typeStreetObj = street.type_street_group ? { id: street.type_street_group_id, type_street: street.type_street_name } : null

      reset({
        street: street.street || '',
        full_street: street.full_street || '',
        type_street_group: typeStreetObj,
        city: cityObj,
        district_big: districtBigObj,
        district: districtObj,
      })
      setSelectedCity(cityObj)
      setSelectedDistrictBig(districtBigObj)
    } else {
      reset({
        street: '',
        full_street: '',
        type_street_group: null,
        city: null,
        district_big: null,
        district: null
      })
      setSelectedCity(null)
      setSelectedDistrictBig(null)
    }
  }, [street, reset])

  // Fetch street types
  const fetchStreetTypes = async ({ search, page, page_size }) => {
    const params = { search, page, page_size }
    return streetTypesApi.getAll(params)
  }

  // Fetch districts_big based on selected city
  const fetchDistrictsBig = async ({ search, page, page_size }) => {
    if (!cityValue) return { data: [] }
    const params = { search, page, page_size }
    if (cityValue?.id) params.city = cityValue.id
    return districtsBigApi.getAll(params)
  }

  // Fetch districts based on selected city and district_big
  const fetchDistricts = async ({ search, page, page_size }) => {
    if (!cityValue) return { data: [] }
    const params = { search, page, page_size }
    if (cityValue?.id) params.city = cityValue.id
    if (districtBigValue?.id) params.district_big = districtBigValue.id
    return districtsApi.getAll(params)
  }

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (formData) => {
      const data = {
        street: formData.street,
        full_street: formData.full_street,
        type_street_group: formData.type_street_group?.id,
        city: formData.city?.id,
        district_big: formData.district_big?.id,
        district: formData.district?.id,
      }
      return isEdit ? streetsApi.update(street.id, data) : streetsApi.create(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['streets'] })
      toastService.success({
        title: t('common.success'),
        description: isEdit ? t('streets.streetUpdated') : t('streets.streetCreated'),
      })
      onClose()
    },
    onError: (error) => {
      toastService.error({
        title: t('common.error'),
        description: error?.message || t('streets.saveError'),
      })
    },
  })

  const onSubmit = (data) => {
    saveMutation.mutate(data)
  }

  const handleClose = () => {
    if (!saveMutation.isPending) {
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? t('streets.editStreet') : t('streets.createStreet')}
      size="xl"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          <Fieldset>
            <Fieldset.Content>
              <Stack gap={4}>
                <Controller
                  name="street"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field }) => (
                    <Input
                      label={`${t('streets.street')} *`}
                      error={errors.street?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="full_street"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={t('streets.fullStreet')}
                      error={errors.full_street?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="type_street_group"
                  control={control}
                  render={({ field }) => (
                    <AutocompleteSelect
                      label={t('streets.typeStreet')}
                      placeholder={t('streets.selectStreetType')}
                      fetchOptions={fetchStreetTypes}
                      getOptionLabel={(option) => option.type_street}
                      getOptionValue={(option) => option.id}
                      error={errors.type_street_group?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <AutocompleteSelect
                      label={t('streets.city')}
                      placeholder={t('streets.selectCity')}
                      fetchOptions={(search) => geolocateApi.getCitiesAutocomplete({ search })}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      error={errors.city?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="district_big"
                  control={control}
                  render={({ field }) => (
                    <AutocompleteSelect
                      label={t('streets.districtBig')}
                      placeholder={t('streets.selectDistrictBig')}
                      fetchOptions={fetchDistrictsBig}
                      getOptionLabel={(option) => option.district_big}
                      getOptionValue={(option) => option.id}
                      error={errors.district_big?.message}
                      isDisabled={!cityValue}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="district"
                  control={control}
                  render={({ field }) => (
                    <AutocompleteSelect
                      label={t('streets.district')}
                      placeholder={t('streets.selectDistrict')}
                      fetchOptions={fetchDistricts}
                      getOptionLabel={(option) => option.district}
                      getOptionValue={(option) => option.id}
                      error={errors.district?.message}
                      isDisabled={!cityValue}
                      {...field}
                    />
                  )}
                />
              </Stack>
            </Fieldset.Content>
          </Fieldset>

          {/* Actions */}
          <HStack justify="flex-end" spacing={3} pt={2}>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={handleClose}
              disabled={saveMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="md"
              loading={saveMutation.isPending}
            >
              {t('common.save')}
            </Button>
          </HStack>
        </Stack>
      </form>
    </Modal>
  )
}
